import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebase';
import { collection, getDocs, query, deleteDoc, doc, where, getDoc, setDoc, writeBatch } from 'firebase/firestore';
import { splitHours3 } from '../utils/memberHours';
import { auth } from '../firebase';
import { usageDocId } from '../utils/weekKey';
import { localToday } from '../utils/dates';
import { testAccountIds, excludeTestPurchases } from '../utils/testAccounts';
import Layout from '../components/Layout';
import RevenuePanel from '../components/reports/RevenuePanel';
import MembershipPanel from '../components/reports/MembershipPanel';
import {
  Box, Typography, Paper, Chip, Alert, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tab, Tabs, Button, IconButton, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SportsIcon from '@mui/icons-material/Sports';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import * as XLSX from 'xlsx';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { MenuItem, Collapse, Autocomplete } from '@mui/material';

// ── Court Usage tab colors ──────────────────────────────────────────────────
// Mirrors Schedule.js: member blue, customer magenta, per-type class colors.
const CU_MEMBER_COLOR = '#2E86C1';
const CU_CUSTOMER_COLOR = '#AD1457';
const CU_CLASS_COLORS = {
  'junior-practice':    '#1E3A5F',
  'junior-private':     '#8E44AD',
  'junior-drop-in':     '#27AE60',
  'junior-evaluation':  '#E67E22',
  'adult-practice':     '#C0392B',
  'league':             '#00838F',
  'block-time':         '#7F8C8D',
};
const cuSessionLabel = (t) => ({
  'junior-practice': 'Junior Practice', 'junior-private': 'Junior Private',
  'junior-drop-in': 'Junior Drop-in', 'junior-evaluation': 'Junior Evaluation',
  'adult-practice': 'Adult Practice', 'league': 'League', 'block-time': 'Block Time',
}[t] || t || '—');
// Court-hours between two HH:MM times; 0 when either is missing/invalid.
const cuHours = (start, end) => {
  const m = (t) => { const [h, mi] = String(t || '').split(':').map(Number); return (h * 60 + (mi || 0)); };
  if (!start || !end) return 0;
  const d = (m(end) - m(start)) / 60;
  return Number.isFinite(d) && d > 0 ? d : 0;
};
const cuCustomerName = (c) => c ? (c.name || `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.email || c.id || '') : '';

// Panels that fetch their own data and need nothing from the club-wide read
// below. Waiting on that read to show them would undo the caching that makes
// the revenue figures appear instantly.
const SELF_LOADING = new Set(['overview', 'membership']);

// Every panel this host can show. Pages pick from these by key; the tab bar is
// built from the page's list, so the order there is the order on screen.
const PANELS = {
  overview:   { label: 'Overview',    icon: null },
  purchases:  { label: 'Purchases',   icon: null },
  staff:      { label: 'Staff',       icon: null },
  expenses:   { label: 'Expenses',    icon: null },
  membership: { label: 'Membership',  icon: null },
  attendance: { label: 'Attendance',  icon: null },
  courtUsage: { label: 'Court Usage', icon: <PeopleIcon /> },
};

/**
 * The club's report panels, hosted for whichever page asked for them.
 *
 * Financials and Reports are the same machinery pointed at different tabs.
 * They were one page until the admin tiles were consolidated, and splitting
 * the panels into two components would have meant duplicating the derivations
 * they share — the P&L needs attendance, purchases needs users, court usage
 * needs both. So the panels stay together and the pages choose from them.
 *
 * `panels` is an ordered list of keys from PANELS below; the tab bar is built
 * from it, so the order on screen is the order given here.
 */
function ClubReports({ panels, title, subtitle, note = null, showStatCards = false }) {
  const [tab, setTab] = useState(0);
  // Which panel is showing, by name. Indices would silently point at a
  // different panel the moment a page reorders its tabs.
  const active = panels[Math.min(tab, panels.length - 1)];
  const [athletes, setAthletes] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [classes, setClasses] = useState([]);
  const [evUsers, setEvUsers] = useState([]);
  // Member Usage tab (court reservation policy observation)
  const [muBookings, setMuBookings] = useState([]);
  const [muUsage, setMuUsage] = useState([]);
  const [muTiers, setMuTiers] = useState([]);
  const [muLoading, setMuLoading] = useState(false);
  const [muLoaded, setMuLoaded] = useState(false);

  const [muCustomers, setMuCustomers] = useState([]);
  const [muRentals, setMuRentals] = useState([]);
  const [muResyncing, setMuResyncing] = useState(false);
  const [muResyncLog, setMuResyncLog] = useState(null);

  // Rebuild every memberUsage doc from the actual non-cancelled bookings.
  // Bookings are the source of truth; usage docs are a rebuildable cache that
  // drifts when bookings are deleted/edited outside the app flow. Idempotent.
  const resyncMemberUsage = async () => {
    if (!window.confirm('Recompute all member usage docs from bookings? Existing usage numbers will be overwritten with recomputed truth.')) return;
    setMuResyncing(true);
    setMuResyncLog(null);
    try {
      const [bSnap, uSnap, tSnap, pbSnap] = await Promise.all([
        getDocs(collection(db, 'memberBookings')),
        getDocs(collection(db, 'memberUsage')),
        getDocs(collection(db, 'membershipTiers')),
        getDocs(collection(db, 'rentalTimeBlocks')),
      ]);
      const primeBlocks = pbSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(p => p.isPrime);
      const tiers = Object.fromEntries(tSnap.docs.map(d => [d.id, d.data()]));

      // True totals per usage-doc id
      const truth = {};
      for (const d of bSnap.docs) {
        const b = d.data();
        if (!b.memberId || !b.date || b.status === 'cancelled') continue;
        const id = usageDocId(b.memberId, b.date);
        const t = truth[id] || (truth[id] = { memberId: b.memberId, regularUsed: 0, primeUsed: 0, weekendUsed: 0, bookings: 0 });
        const s = splitHours3(b.startTime, b.endTime, b.date, primeBlocks, (b.courts || []).length || 1);
        t.regularUsed += s.regularHours;
        t.primeUsed += s.primeHours;
        t.weekendUsed += s.weekendHours;
        t.bookings += 1;
      }

      const round1 = (n) => Math.round(n * 100) / 100;
      const changes = [];
      const batch = writeBatch(db);
      let writes = 0;

      // Existing docs: correct drift; orphans (no surviving bookings) zeroed
      for (const d of uSnap.docs) {
        const cur = d.data();
        const t = truth[d.id] || { memberId: cur.memberId || d.id.split('_')[0], regularUsed: 0, primeUsed: 0, weekendUsed: 0, bookings: 0 };
        const diff = ['regularUsed', 'primeUsed', 'weekendUsed'].some(k => round1(cur[k] || 0) !== round1(t[k]));
        if (diff) {
          changes.push(`${d.id}: R ${round1(cur.regularUsed || 0)}→${round1(t.regularUsed)} · P ${round1(cur.primeUsed || 0)}→${round1(t.primeUsed)} · W ${round1(cur.weekendUsed || 0)}→${round1(t.weekendUsed)} (${t.bookings} bookings)`);
          const user = evUsers.find(u => u.id === t.memberId);
          const tier = user?.membership?.tierId ? tiers[user.membership.tierId] : null;
          batch.set(doc(db, 'memberUsage', d.id), {
            regularUsed: round1(t.regularUsed), primeUsed: round1(t.primeUsed), weekendUsed: round1(t.weekendUsed),
            ...(tier ? {
              regularAlloc: Number(tier.weeklyRegularHours) || 0,
              primeAlloc: Number(tier.weeklyPrimeHours) || 0,
              weekendAlloc: Number(tier.weeklyWeekendHours) || 0,
            } : {}),
            resyncedAt: new Date(),
          }, { merge: true });
          writes += 1;
        }
        delete truth[d.id];
      }
      // Weeks with bookings but no usage doc at all: create
      for (const [id, t] of Object.entries(truth)) {
        changes.push(`${id}: created — R ${round1(t.regularUsed)} · P ${round1(t.primeUsed)} · W ${round1(t.weekendUsed)} (${t.bookings} bookings)`);
        batch.set(doc(db, 'memberUsage', id), {
          memberId: t.memberId,
          regularUsed: round1(t.regularUsed), primeUsed: round1(t.primeUsed), weekendUsed: round1(t.weekendUsed),
          resyncedAt: new Date(),
        }, { merge: true });
        writes += 1;
      }

      if (writes > 0) await batch.commit();
      setMuResyncLog(changes.length ? changes : ['No drift found — all usage docs match bookings.']);
      setMuLoaded(false); // reload the tab data
    } catch (e) {
      console.error('resync failed:', e);
      setMuResyncLog([`Resync failed: ${e.message}`]);
    }
    setMuResyncing(false);
  };

  // By name, not by index. This read used to fire on tab 6, which was Court
  // Usage back when all seven panels shared one page; Reports now shows three,
  // so the index never came round and the panel sat empty behind muLoaded.
  useEffect(() => {
    if (active !== 'courtUsage' || muLoaded) return;
    setMuLoading(true);
    Promise.all([
      getDocs(collection(db, 'memberBookings')),
      getDocs(collection(db, 'memberUsage')),
      getDocs(collection(db, 'membershipTiers')),
      getDocs(collection(db, 'customers')).catch(() => ({ docs: [] })),
      getDocs(collection(db, 'rentalBookings')).catch(() => ({ docs: [] })),
    ]).then(([bSnap, uSnap, tSnap, cSnap, rSnap]) => {
      setMuBookings(bSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setMuUsage(uSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setMuTiers(tSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setMuCustomers(cSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setMuRentals(rSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setMuLoaded(true);
    }).catch(console.error).finally(() => setMuLoading(false));
  }, [active, muLoaded]);

  // Per-member aggregation for the Member Usage tab
  const memberUsageRows = (() => {
    if (!muLoaded) return [];
    const todayStr = localToday();
    const byMember = {};
    for (const b of muBookings) {
      if (!b.memberId) continue;
      const m = byMember[b.memberId] || (byMember[b.memberId] = {
        memberId: b.memberId, bookings: 0, cancelled: 0,
        regular: 0, prime: 0, weekend: 0, overage: 0, flags: 0, flagged: [],
        lastDate: '',
      });
      if (b.status === 'cancelled') { m.cancelled += 1; continue; }
      m.bookings += 1;
      m.regular += b.regularHours || 0;
      m.prime += b.primeHours || 0;
      m.weekend += b.weekendHours || 0;
      m.overage += b.overageAmount || 0;
      if (Array.isArray(b.policyFlags) && b.policyFlags.length) { m.flags += b.policyFlags.length; m.flagged.push(b); }
      if ((b.date || '') > m.lastDate) m.lastDate = b.date;
    }
    return Object.values(byMember).map(m => {
      const user = evUsers.find(u => u.id === m.memberId) || {};
      const tier = muTiers.find(t => t.id === user.membership?.tierId);
      const usage = muUsage.find(u => u.id === usageDocId(m.memberId, todayStr)) || {};
      return {
        ...m,
        name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || m.memberId,
        tierName: tier?.name || (user.membership?.tierId ? user.membership.tierId : '—'),
        tier,
        weekRegular: usage.regularUsed || 0,
        weekPrime: usage.primeUsed || 0,
        weekWeekend: usage.weekendUsed || 0,
      };
    }).sort((a, b) => (b.regular + b.prime + b.weekend) - (a.regular + a.prime + a.weekend));
  })();

  // Customer reservations: schedule events linked to a customer via the New
  // Event dialog, plus court-rental bookings carrying a customerId. Hours are
  // court-hours (duration × courts; schedule events are one doc per court).
  const customerUsageRows = (() => {
    if (!muLoaded) return [];
    const byCust = {};
    const bump = (custId, custName, hours, date, kind) => {
      const c = byCust[custId] || (byCust[custId] = { customerId: custId, name: '', events: 0, rentals: 0, hours: 0, lastDate: '' });
      if (kind === 'event') c.events += 1; else c.rentals += 1;
      c.hours += hours;
      if ((date || '') > c.lastDate) c.lastDate = date;
      if (custName && !c.name) c.name = custName;
    };
    for (const cl of classes) {
      if (!cl.customerId || cl.status === 'canceled') continue;
      const hrs = cl.durationMinutes ? cl.durationMinutes / 60 : cuHours(cl.startTime, cl.endTime);
      bump(cl.customerId, cl.customerName, hrs, cl.date, 'event');
    }
    for (const rb of muRentals) {
      if (!rb.customerId || rb.status === 'cancelled' || rb.status === 'canceled') continue;
      const courts = Array.isArray(rb.courts) ? rb.courts.length : 1;
      bump(rb.customerId, rb.customerName, cuHours(rb.startTime, rb.endTime) * courts, rb.date, 'rental');
    }
    return Object.values(byCust).map(c => ({
      ...c,
      name: cuCustomerName(muCustomers.find(x => x.id === c.customerId)) || c.name || c.customerId,
    })).sort((a, b) => b.hours - a.hours);
  })();

  // Class court usage, tracked at the class level only — no per-player or
  // per-member attendance. One row per session type; customer-linked events
  // are excluded here (they're counted under Customers above).
  const classUsageRows = (() => {
    if (!muLoaded) return [];
    const byType = {};
    for (const cl of classes) {
      if (cl.customerId || cl.status === 'canceled') continue;
      const t = cl.sessionType || 'other';
      const r = byType[t] || (byType[t] = { type: t, count: 0, hours: 0, lastDate: '' });
      r.count += 1;
      r.hours += cl.durationMinutes ? cl.durationMinutes / 60 : cuHours(cl.startTime, cl.endTime);
      if ((cl.date || '') > r.lastDate) r.lastDate = cl.date;
    }
    return Object.values(byType).sort((a, b) => b.hours - a.hours);
  })();

  const [coaches, setCoaches] = useState([]);
  const [seasonSel, setSeasonSel] = useState(-1); // -1 = auto (current/latest)
  // Unified search: one box on the tab row, filters whichever tab is active.
  const [search, setSearch] = useState('');
  const _q = search.trim().toLowerCase();
  const qMatch = (...vals) => !_q || vals.some(v => String(v || '').toLowerCase().includes(_q));
  const [expandedAthleteId, setExpandedAthleteId] = useState(null);
  const [plans, setPlans] = useState([]);
  const [overhead, setOverhead] = useState('0');
  const [overheadSaving, setOverheadSaving] = useState(false);
  const [sessionFee, setSessionFee] = useState('30');
  const [adultFee, setAdultFee] = useState(''); // blank = use junior session fee
  const [importedClasses, setImportedClasses] = useState([]);
  const [importMsg, setImportMsg] = useState('');
  const [loading, setLoading] = useState(true);
  // ── Club & operational expenses ───────────────────────────────────────────
  // The Financials tab reports a calendar year-to-date — income, coach pay,
  // overhead and these expenses all share pnlYear. Seasons scope the other tabs.
  const [expenses, setExpenses] = useState([]);
  // Everything already used in a given field, for the pickers on the expense
  // form. Derived rather than configured: expenses arrive by CSV import as well
  // as by hand, so a fixed list would go stale the first time an import carried
  // something new. Deduplicated case-insensitively, keeping the first spelling
  // seen, so an existing near-duplicate doesn't appear twice in the menu.
  const usedValues = (rows, field) => {
    const seen = new Map();
    rows.forEach(r => {
      const v = String(r[field] || '').trim();
      if (v && !seen.has(v.toLowerCase())) seen.set(v.toLowerCase(), v);
    });
    return [...seen.values()].sort((a, b) => a.localeCompare(b));
  };
  const expenseCategories = useMemo(() => usedValues(expenses, 'category'), [expenses]);
  const expenseVendors = useMemo(() => usedValues(expenses, 'vendor'), [expenses]);
  const [expenseDialog, setExpenseDialog] = useState(false);
  const [pnlYear, setPnlYear] = useState(() => Number(localToday().slice(0, 4)));
  const [expenseEdit, setExpenseEdit] = useState(null); // null = closed, {} = new
  const [expenseSaving, setExpenseSaving] = useState(false);
  const [expenseMsg, setExpenseMsg] = useState('');
  const [expenseImporting, setExpenseImporting] = useState(false);
  const [expensePreview, setExpensePreview] = useState(null); // dry-run rows
  // Which budget the expense table is showing: null = both. Set by clicking the
  // Corona/Torch tiles. Only ever narrows the TABLE — see expenseTotals.
  const [expenseBudget, setExpenseBudget] = useState(null);
  // ── Income (cash basis) ───────────────────────────────────────────────────
  const [tournaments, setTournaments] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [leagueRegs, setLeagueRegs] = useState([]);
  const [incomeDialog, setIncomeDialog] = useState(false);
  const [incomeSourceSel, setIncomeSourceSel] = useState(null); // drill-down key

  useEffect(() => {
    const fetchData = async () => {
      const [athleteSnap, purchaseSnap, attendanceSnap, classSnap, coachSnap, planSnap, settingsSnap, allUserSnap] = await Promise.all([
        getDocs(collection(db, 'athletes')),
        getDocs(collection(db, 'purchases')),
        getDocs(collection(db, 'attendance')),
        getDocs(collection(db, 'classes')),
        getDocs(query(collection(db, 'users'), where('roles', 'array-contains-any', ['coach', 'assistant_coach']))),
        getDocs(collection(db, 'plans')),
        getDoc(doc(db, 'settings', 'reports')),
        getDocs(collection(db, 'users')),
      ]);
      setEvUsers(allUserSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setAthletes(athleteSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      const _pd = x => (x?.purchaseDate?.seconds || x?.createdAt?.seconds || 0);
      setPurchases(purchaseSnap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => _pd(b) - _pd(a)));
      setAttendance(attendanceSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setPlans(planSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      try {
        const impSnap = await getDocs(collection(db, 'importedClasses'));
        setImportedClasses(impSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) { /* collection may not exist yet */ }
      try {
        const expSnap = await getDocs(collection(db, 'expenses'));
        setExpenses(expSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) { /* collection may not exist yet */ }
      try {
        const [tSnap, rSnap] = await Promise.all([
          getDocs(collection(db, 'tournaments')),
          getDocs(query(collection(db, 'registrations'), where('status', '==', 'active'))),
        ]);
        setTournaments(tSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setRegistrations(rSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) { /* tournaments optional */ }
      try {
        const invSnap = await getDocs(collection(db, 'customerInvoices'));
        setInvoices(invSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) { /* collection may not exist yet */ }
      try {
        const lrSnap = await getDocs(collection(db, 'leagueRegistrations'));
        setLeagueRegs(lrSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) { /* collection may not exist yet */ }
      if (settingsSnap.exists()) { setOverhead(String(settingsSnap.data().overheadPerPractice ?? 0)); setSessionFee(String(settingsSnap.data().sessionFee ?? 30)); setAdultFee(settingsSnap.data().adultSessionFee != null ? String(settingsSnap.data().adultSessionFee) : ''); }
      setClasses(classSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setCoaches(coachSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    };
    fetchData();
  }, []);

  // Derived stats
  const activeAthletes = athletes.filter(a => a.athleteType !== 'unknown');
  const juniorAthletes = athletes.filter(a => a.athleteType === 'junior');
  const adultAthletes = athletes.filter(a => a.athleteType === 'adult');
  const leads = athletes.filter(a => a.athleteType === 'unknown');
  const zeroBalance = activeAthletes.filter(a => (a.sessionBalance || 0) === 0);
  const lowBalance = activeAthletes.filter(a => (a.sessionBalance || 0) > 0 && (a.sessionBalance || 0) <= 2);
  const totalSessions = activeAthletes.reduce((sum, a) => sum + (a.sessionBalance || 0), 0);
  // Test accounts buy things too — $0 and $1 rows from checkout testing that
  // would otherwise read as sales in the revenue tile and the transaction
  // count. Excluded from every total below; nothing is deleted, so unsetting
  // the flag brings them back. See src/utils/testAccounts.js.
  const testIds = useMemo(() => testAccountIds(evUsers, athletes), [evUsers, athletes]);
  const realPurchases = useMemo(() => excludeTestPurchases(purchases, testIds), [purchases, testIds]);
  const totalRevenue = realPurchases.filter(p => p.paymentStatus === 'paid').reduce((sum, p) => sum + (p.amountPaid || 0), 0);
  const pendingRevenue = realPurchases.filter(p => p.paymentStatus === 'pending').reduce((sum, p) => sum + (p.amountPaid || 0), 0);
  const completedClasses = classes.filter(c => c.status === 'completed').length;

  // ── Season attendance report ──
  const _now = new Date();
  const _todayStr = `${_now.getFullYear()}-${String(_now.getMonth()+1).padStart(2,'0')}-${String(_now.getDate()).padStart(2,'0')}`;
  const juniorSeasonClasses = [...classes, ...importedClasses].filter(c => (c.sessionType || '').toLowerCase().startsWith('junior') && c.date && c.status !== 'canceled');
  const adultSeasonClasses = [...classes, ...importedClasses].filter(c => (c.sessionType || '').toLowerCase().startsWith('adult') && c.date && c.status !== 'canceled');
  const _dates = [...new Set([...juniorSeasonClasses, ...adultSeasonClasses].map(c => c.date))].sort();
  // Fixed 13-week (91-day) seasons anchored at Season 37 = Mon 2026-05-04.
  const _anchor = new Date('2026-05-04T00:00:00');
  const _fmt = dt => `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`;
  const _seasonName = num => {
    const off = num - 37;
    const cycle = ['Summer', 'Fall', 'Winter', 'Spring'];
    return `${cycle[((off % 4) + 4) % 4]} ${2026 + Math.floor((off + 2) / 4)}`;
  };
  const _numFor = d => 37 + Math.floor((new Date(d + 'T00:00:00') - _anchor) / (91 * 86400000));
  const seasons = [];
  if (_dates.length) {
    const first = _numFor(_dates[0]);
    const last = Math.max(_numFor(_dates[_dates.length - 1]), _numFor(_todayStr));
    for (let n = first; n <= last; n++) {
      const st = new Date(_anchor); st.setDate(st.getDate() + (n - 37) * 91);
      const en = new Date(st); en.setDate(en.getDate() + 90);
      const range = { num: n, name: _seasonName(n), start: _fmt(st), end: _fmt(en) };
      if (n >= 37 && _dates.some(d => d >= range.start && d <= range.end)) seasons.push(range);
    }
  }
  const _autoIdx = (() => {
    const i = seasons.findIndex(r => r.start <= _todayStr && r.end >= _todayStr);
    return i >= 0 ? i : seasons.length - 1;
  })();
  const season = seasons.length ? seasons[(seasonSel >= 0 && seasonSel < seasons.length) ? seasonSel : _autoIdx] : null;
  const seasonClasses = season
    ? juniorSeasonClasses.filter(c => c.date >= season.start && c.date <= season.end).sort((a, b) => a.date.localeCompare(b.date) || (a.startTime || '').localeCompare(b.startTime || ''))
    : [];
  // The P&L is scoped to a calendar year-to-date, independent of seasons, so that
  // income and coach pay line up with the club/operational expenses below (which
  // are kept by calendar year). Every other tab here is still season-scoped.
  const pnlYearStart = `${pnlYear}-01-01`;
  const pnlYearEnd = `${pnlYear}-12-31`;
  const pnlClasses = [...juniorSeasonClasses, ...adultSeasonClasses]
    .filter(c => c.date >= pnlYearStart && c.date <= pnlYearEnd)
    .sort((a, b) => a.date.localeCompare(b.date) || (a.startTime || '').localeCompare(b.startTime || ''));
  const weekNum = d => season ? Math.floor((new Date(d + 'T00:00:00') - new Date(season.start + 'T00:00:00')) / (7 * 86400000)) + 1 : 0;
  // Week-of-year for the P&L table, which no longer has a season to count from.
  const pnlWeekNum = d => Math.floor((new Date(d + 'T00:00:00') - new Date(pnlYearStart + 'T00:00:00')) / (7 * 86400000)) + 1;
  const _classIds = new Set(seasonClasses.map(c => c.id));
  const _classById = Object.fromEntries(seasonClasses.map(c => [c.id, c]));
  const _byAthlete = {};
  attendance.forEach(a => {
    if (a.type === 'coach' || !a.checkedInAt || !_classIds.has(a.classId)) return;
    (_byAthlete[a.athleteId] = _byAthlete[a.athleteId] || new Set()).add(a.classId);
  });
  const seasonPastCount = seasonClasses.filter(c => c.date <= _todayStr).length;
  const seasonRows = athletes
    .filter(a => (a.athleteType || '').toLowerCase() === 'junior' && !a.archived && _byAthlete[a.id])
    .map(a => {
      const set = _byAthlete[a.id];
      const attended = [...set].filter(id => _classById[id].date <= _todayStr).length;
      return { ...a, _set: set, attended, pct: seasonPastCount ? attended / seasonPastCount : 0 };
    })
    .filter(a => qMatch(`${a.firstName} ${a.lastName}`))
    .sort((x, y) => y.attended - x.attended || `${x.firstName}${x.lastName}`.localeCompare(`${y.firstName}${y.lastName}`));

  const buildSeasonGrid = () => {
    const head = ['#', 'Athlete', ...seasonClasses.map(c => `S${season.num}W${weekNum(c.date)} ${c.date.slice(5).replace('-', '/')}`), 'Used', 'Balance', 'Attendance %'];
    const body = seasonRows.map((r, i) => [
      i + 1,
      `${r.firstName} ${r.lastName}${r.schoolName ? ` (${r.schoolName})` : ''}`,
      ...seasonClasses.map(c => (r._set.has(c.id) ? 'a' : '')),
      r.attended, r.sessionBalance || 0, Math.round(r.pct * 1000) / 10 + '%',
    ]);
    return [head, ...body];
  };
  const exportSeasonXlsx = () => {
    const ws = XLSX.utils.aoa_to_sheet(buildSeasonGrid());
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, season.name);
    XLSX.writeFile(wb, `${season.name.replace(' ', '_')}_S${season.num}_Attendance.xlsx`);
  };
  const exportSeasonCsv = () => {
    const csv = buildSeasonGrid().map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = `${season.name.replace(' ', '_')}_S${season.num}_Attendance.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  // ── Per-practice P&L ──
  // ── Expense helpers ───────────────────────────────────────────────────────
  // A recurring expense covers `months` consecutive months starting at `date`.
  // Only the months landing inside the selected calendar year are counted, so a
  // 24-month port-a-potty contract contributes 12 months to each year it spans.
  const expenseMonthsInYear = (e, year) => {
    const start = new Date(`${(e.date || '').slice(0, 10)}T00:00:00`);
    if (isNaN(start)) return 0;
    const startIdx = start.getFullYear() * 12 + start.getMonth();
    const term = Math.max(1, Math.round(Number(e.months) || 0));
    const yearStart = year * 12;
    const lo = Math.max(startIdx, yearStart);
    const hi = Math.min(startIdx + term, yearStart + 12);
    return Math.max(0, hi - lo);
  };

  const expenseAmountForYear = (e, year) => {
    if (e.recurring) return Math.round((Number(e.monthlyAmount) || 0) * expenseMonthsInYear(e, year) * 100) / 100;
    return (e.date || '').slice(0, 4) === String(year) ? (Number(e.amount) || 0) : 0;
  };

  const pnlYears = [...new Set([
    Number(localToday().slice(0, 4)),
    ...expenses.flatMap(e => {
      const y = Number((e.date || '').slice(0, 4));
      if (!y) return [];
      const term = e.recurring ? Math.max(1, Math.round(Number(e.months) || 0)) : 1;
      const endY = new Date(`${(e.date || '').slice(0, 10)}T00:00:00`);
      endY.setMonth(endY.getMonth() + term - 1);
      return Array.from({ length: endY.getFullYear() - y + 1 }, (_, i) => y + i);
    }),
    // Years that have practices, so the P&L can be run for them even with no expenses on file
    ...[...juniorSeasonClasses, ...adultSeasonClasses].map(c => Number((c.date || '').slice(0, 4))).filter(Boolean),
    ...invoices.filter(i => i.paid).map(i => Number(String(i.paidDate || '').slice(0, 4))).filter(Boolean),
  ])].sort((a, b) => b - a);

  const yearExpenses = expenses
    .map(e => ({ ...e, yearAmount: expenseAmountForYear(e, pnlYear) }))
    .filter(e => e.yearAmount > 0)
    .sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));

  // Deliberately computed off the UNFILTERED year: the tiles are the year's
  // three real numbers, and they have to keep saying so while one of them is
  // selected. Filtering these too would blank the tile you did not click and
  // leave the one you did agreeing with itself, which tells you nothing.
  const expenseTotals = yearExpenses.reduce((t, e) => ({
    all: t.all + e.yearAmount,
    Corona: t.Corona + (e.budget === 'Corona' ? e.yearAmount : 0),
    Torch: t.Torch + (e.budget === 'Torch' ? e.yearAmount : 0),
  }), { all: 0, Corona: 0, Torch: 0 });

  // What the table actually lists, and the sum of it — the footer has to agree
  // with the rows above it rather than with the Total tile.
  const shownExpenses = expenseBudget
    ? yearExpenses.filter(e => e.budget === expenseBudget)
    : yearExpenses;
  const shownExpenseTotal = expenseBudget ? expenseTotals[expenseBudget] : expenseTotals.all;

  // ── Income, cash basis ────────────────────────────────────────────────────
  // Money actually collected in pnlYear, NOT the per-practice figure in the table
  // below (which imputes attendees x session fee). Mixing the two would double
  // count: a punchcard is collected once but consumed over 8 practices.
  const _purchaseYear = (p) => {
    const secs = p.purchaseDate?.seconds ?? p.createdAt?.seconds;
    return secs ? new Date(secs * 1000).getFullYear() : null;
  };
  const _planOf = (p) => plans.find(x => x.id === p.planId);

  const incomeCategory = (p) => {
    if (p.purchaseType === 'day_pass') return 'dayPass';
    // Court time sold to a member. Without this it falls through to the
    // planName heuristic and lands in junior training.
    if (p.purchaseType === 'court_time' || p.source === 'court_time') return 'membership';
    if (p.source === 'membership' || /^membership_/.test(p.purchaseType || '') || p.tierId) return 'membership';
    const at = (_planOf(p)?.athleteType || '').toLowerCase();
    if (at === 'adult') return 'adultTraining';
    if (at === 'junior') return 'juniorTraining';
    // No plan link (legacy/manual rows) — fall back to the plan name
    return /adult/i.test(p.planName || '') ? 'adultTraining' : 'juniorTraining';
  };

  // Money that came in, whatever happened to it later: a refunded purchase was
  // still paid on its purchase date. Dropping it here rewrote closed months —
  // a January renewal refunded in February vanished from January.
  const _moneyIn = (p) => ['paid', 'refunded', 'partial_refund'].includes(p.paymentStatus)
    && (Number(p.amountPaid) || 0) > 0;
  const yearPurchases = realPurchases.filter(p => _moneyIn(p) && _purchaseYear(p) === pnlYear);
  // Refunds are their own entries on the day they were given, as league
  // refunds are below, and land in the year of the refund, not the payment.
  const _refundDay = (p) => new Date((p.refundDate?.seconds ?? p.purchaseDate?.seconds
    ?? p.createdAt?.seconds ?? 0) * 1000).toISOString().slice(0, 10);
  const yearRefunds = realPurchases.filter(p => _moneyIn(p) && (Number(p.refundAmount) || 0) > 0
    && _refundDay(p).slice(0, 4) === String(pnlYear));

  // Tournament revenue mirrors TournamentAdminFinancial: active registrations,
  // Stripe amount_paid when present, else the division/tournament price.
  const tournamentIncomeRows = tournaments
    .filter(t => String(t.date || '').slice(0, 4) === String(pnlYear))
    .map(t => {
      const prices = typeof t.prices === 'string'
        ? (() => { try { return JSON.parse(t.prices); } catch { return {}; } })()
        : (t.prices || {});
      const tRegs = registrations.filter(r => r.tournament_id === t.id);
      const amount = tRegs.reduce((acc, r) => {
        if (r.amount_paid && r.amount_paid > 0) return acc + r.amount_paid / 100;
        const fee = prices[r.division] != null ? Number(prices[r.division]) : (t.price_dollars || 0);
        return acc + (isNaN(fee) ? 0 : fee);
      }, 0);
      return { id: t.id, date: t.date, label: t.name || 'Tournament', count: tRegs.length, amount };
    })
    .filter(r => r.count > 0);

  const _catRows = (cat) => yearPurchases.filter(p => incomeCategory(p) === cat).map(p => ({
    id: p.id,
    date: new Date((p.purchaseDate?.seconds ?? p.createdAt?.seconds ?? 0) * 1000).toISOString().slice(0, 10),
    label: p.tierName || p.planName || '—',
    who: p.athleteName || p.email || p.customerEmail || '',
    count: 1,
    amount: Number(p.amountPaid) || 0,
  })).concat(yearRefunds.filter(p => incomeCategory(p) === cat).map(p => ({
    id: p.id + ':refund',
    date: _refundDay(p),
    label: `${p.tierName || p.planName || '—'} — refund`,
    who: p.athleteName || p.email || p.customerEmail || '',
    count: 0,
    amount: -(Number(p.refundAmount) || 0),
  }))).sort((a, b) => b.date.localeCompare(a.date));

  // Cash basis: an invoice is income when it's paid, on its paidDate — not when
  // it was raised. Unpaid invoices are receivables and stay out of the P&L.
  // Overage invoices are court time sold to a member — membership income, not
  // customer income. Split them out so the Customers line stays third-party.
  const _isOverageInv = (i) => i.source === 'member_overage';
  const overageIncomeRows = invoices
    .filter(i => _isOverageInv(i) && i.paid && String(i.paidDate || '').slice(0, 4) === String(pnlYear) && (Number(i.amount) || 0) !== 0)
    .map(i => ({
      id: i.id, date: i.paidDate, label: i.description || 'Court time',
      who: i.customerName || '', count: 1, amount: Number(i.amount) || 0,
    }))
    .sort((a, b) => String(b.date).localeCompare(String(a.date)));

  const invoiceIncomeRows = invoices
    .filter(i => !_isOverageInv(i) && i.paid && String(i.paidDate || '').slice(0, 4) === String(pnlYear) && (Number(i.amount) || 0) !== 0)
    .map(i => ({
      id: i.id,
      date: i.paidDate,
      label: i.description || '—',
      who: i.customerName || '',
      count: 1,
      amount: Number(i.amount) || 0,
    }))
    .sort((a, b) => String(b.date).localeCompare(String(a.date)));

  // League registrations, cash basis like the invoices above: income on the day
  // it was paid, not the day someone signed up. Wait-listed players are never
  // charged and free leagues record nothing, so both drop out on amount alone.
  //
  // amountPaid is stored in CENTS by the Stripe webhook — everything else in
  // this P&L is already dollars.
  // Local date parts, not toISOString — a payment taken after 8pm in Orlando is
  // already tomorrow in UTC and would land in the wrong year.
  const _localDay = (sec) => {
    const d = new Date((sec || 0) * 1000);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  const leagueIncomeRows = leagueRegs
    .flatMap(r => {
      const paid = Number(r.amountPaid) || 0;
      const refunded = Number(r.amountRefunded) || 0;
      const who = [r.firstName, r.lastName].filter(Boolean).join(' ') || r.email || '';
      const rows = [];
      if (paid > 0 && (r.paymentStatus === 'paid' || r.paymentStatus === 'refunded')) {
        rows.push({ id: r.id, date: _localDay(r.paidAt?.seconds ?? r.createdAt?.seconds),
          label: r.leagueName || 'League', who, count: 1, amount: paid / 100 });
      }
      // A refund is its own entry on the day it was given, not a correction to
      // the original. Netting it against the payment would quietly rewrite a
      // closed month when a refund lands in the next one.
      if (refunded > 0) {
        rows.push({ id: r.id + ':refund', date: _localDay(r.refundedAt?.seconds ?? r.paidAt?.seconds),
          label: `${r.leagueName || 'League'} — refund`, who, count: 0, amount: -refunded / 100 });
      }
      return rows;
    })
    .filter(r => r.date.slice(0, 4) === String(pnlYear))
    .sort((a, b) => b.date.localeCompare(a.date));

  const incomeSources = [
    { key: 'membership', label: 'Memberships', rows: [..._catRows('membership'), ...overageIncomeRows] },
    { key: 'juniorTraining', label: 'Junior training', rows: _catRows('juniorTraining') },
    { key: 'adultTraining', label: 'Adult training', rows: _catRows('adultTraining') },
    { key: 'dayPass', label: 'Day passes', rows: _catRows('dayPass') },
    { key: 'tournament', label: 'Tournament registrations', rows: tournamentIncomeRows },
    { key: 'customers', label: 'Customers', rows: invoiceIncomeRows },
    { key: 'league', label: 'League registrations', rows: leagueIncomeRows },
  ].map(s => ({
    ...s,
    count: s.rows.reduce((t, r) => t + (r.count || 1), 0),
    amount: Math.round(s.rows.reduce((t, r) => t + r.amount, 0) * 100) / 100,
  }));

  const incomeTotal = Math.round(incomeSources.reduce((t, s) => t + s.amount, 0) * 100) / 100;

  const saveExpense = async () => {
    const f = expenseEdit || {};
    setExpenseSaving(true);
    try {
      const recurring = !!f.recurring;
      const qty = Number(f.qty) || 0;
      const unitCost = Number(f.unitCost) || 0;
      const payload = {
        date: f.date || localToday(),
        budget: f.budget || 'Corona',
        category: (f.category || '').trim(),
        vendor: (f.vendor || '').trim(),
        notes: (f.notes || '').trim(),
        recurring,
        qty: recurring ? 0 : qty,
        unitCost: recurring ? 0 : unitCost,
        amount: recurring ? 0 : (f.amount !== undefined && f.amount !== '' ? Number(f.amount) : Math.round(qty * unitCost * 100) / 100),
        monthlyAmount: recurring ? (Number(f.monthlyAmount) || 0) : 0,
        months: recurring ? (Number(f.months) || 12) : 0,
        updatedAt: new Date(),
        updatedBy: auth.currentUser?.uid || null,
      };
      const ref = f.id ? doc(db, 'expenses', f.id) : doc(collection(db, 'expenses'));
      await setDoc(ref, payload, { merge: true });
      setExpenses(prev => {
        const next = prev.filter(x => x.id !== ref.id);
        return [...next, { id: ref.id, ...payload }];
      });
      setExpenseEdit(null);
      setExpenseMsg('Saved.');
    } catch (err) {
      setExpenseMsg(err.message || 'Save failed.');
    } finally { setExpenseSaving(false); }
  };

  const removeExpense = async (e) => {
    setExpenseSaving(true);
    try {
      await deleteDoc(doc(db, 'expenses', e.id));
      setExpenses(prev => prev.filter(x => x.id !== e.id));
      setExpenseMsg('Deleted.');
    } catch (err) {
      setExpenseMsg(err.message || 'Delete failed.');
    } finally { setExpenseSaving(false); }
  };

  // ── Spreadsheet import ────────────────────────────────────────────────────
  // Reads the "20xx Expenses" sheets from the maintained workbook. Each sheet is
  // one-off rows, then a "Monthly" block of recurring costs, then summary rows
  // (Total/Corona/Torch) and a "PoP Detail" section — both of which we stop at.
  const _expenseKey = (r) => {
    const raw = [r.date, r.budget, r.category, r.vendor, r.recurring ? `m${r.months}x${r.monthlyAmount}` : r.amount].join('|');
    let h = 5381;
    for (let i = 0; i < raw.length; i++) h = ((h * 33) ^ raw.charCodeAt(i)) >>> 0;
    return `imp_${r.date}_${h.toString(36)}`;
  };

  const parseExpenseWorkbook = async (file) => {
    const wb = XLSX.read(await file.arrayBuffer(), { type: 'array', cellDates: true });
    const out = [];
    for (const name of wb.SheetNames) {
      const year = Number((name.match(/(20\d\d)/) || [])[1]);
      if (!year) continue;
      const grid = XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1, blankrows: true });
      let mode = 'oneoff';
      for (const row of grid.slice(1)) {
        const c0 = String(row[0] ?? '').trim();
        if (/^pop detail$/i.test(c0)) break;
        if (/^monthly$/i.test(c0)) { mode = 'monthly'; continue; }
        const budget = String(row[1] ?? '').trim();
        if (!/^(corona|torch)$/i.test(budget)) continue; // skips blanks + Total/Corona/Torch summary rows
        const norm = budget.toLowerCase() === 'corona' ? 'Corona' : 'Torch';
        const category = String(row[2] ?? '').trim();
        const vendor = String(row[3] ?? '').trim();
        const notes = String(row[4] ?? '').trim();
        const qty = Number(row[5]) || 0;
        const unitCost = Number(row[6]) || 0;
        const amountCell = Number(row[7]);
        if (mode === 'monthly') {
          // In the Monthly block, Qty is billings-per-year and Amount is the annual
          // total — e.g. port-a-potty is 24 x $117 = 2 units every month for 12
          // months, NOT a 24-month term. So every row is a 12-month recurrence for
          // the sheet's year at annual/12 per month.
          const annual = Number.isFinite(amountCell) && amountCell !== 0 ? amountCell : Math.round(qty * unitCost * 100) / 100;
          if (!annual) continue;
          out.push({ date: `${year}-01-01`, budget: norm, category, vendor, notes,
            recurring: true, months: 12, monthlyAmount: Math.round((annual / 12) * 100) / 100,
            qty: 0, unitCost: 0, amount: 0, sheet: name });
        } else {
          const d = row[0] instanceof Date ? row[0] : null;
          if (!d) continue;
          const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          const amount = Number.isFinite(amountCell) && amountCell !== 0
            ? amountCell : Math.round(qty * unitCost * 100) / 100;
          if (!amount) continue;
          out.push({ date, budget: norm, category, vendor, notes,
            recurring: false, qty, unitCost, amount, months: 0, monthlyAmount: 0, sheet: name });
        }
      }
    }
    return out.map(r => ({ ...r, _id: _expenseKey(r) }));
  };

  const handleExpenseFile = async (file) => {
    if (!file) return;
    setExpenseMsg('');
    try {
      const rows = await parseExpenseWorkbook(file);
      if (!rows.length) { setExpenseMsg('No expense rows found in that workbook.'); return; }
      const existing = new Set(expenses.map(e => e.id));
      setExpensePreview({ rows, isNew: rows.filter(r => !existing.has(r._id)).length });
    } catch (err) {
      setExpenseMsg(err.message || 'Could not read that file.');
    }
  };

  const commitExpenseImport = async () => {
    const rows = expensePreview?.rows || [];
    setExpenseImporting(true);
    try {
      const stamp = { updatedAt: new Date(), updatedBy: auth.currentUser?.uid || null, importedFrom: 'spreadsheet' };
      for (let i = 0; i < rows.length; i += 400) {
        const batch = writeBatch(db);
        for (const r of rows.slice(i, i + 400)) {
          const { _id, sheet, ...data } = r;
          batch.set(doc(db, 'expenses', _id), { ...data, ...stamp }, { merge: true });
        }
        await batch.commit();
      }
      const merged = new Map(expenses.map(e => [e.id, e]));
      for (const r of rows) {
        const { _id, sheet, ...data } = r;
        merged.set(_id, { id: _id, ...data });
      }
      setExpenses([...merged.values()]);
      setExpenseMsg(`Imported ${rows.length} expense${rows.length === 1 ? '' : 's'}.`);
      setExpensePreview(null);
    } catch (err) {
      setExpenseMsg(err.message || 'Import failed.');
    } finally { setExpenseImporting(false); }
  };

  const saveOverhead = async () => {
    setOverheadSaving(true);
    try { await setDoc(doc(db, 'settings', 'reports'), { overheadPerPractice: parseFloat(overhead) || 0, sessionFee: parseFloat(sessionFee) || 0, adultSessionFee: adultFee === '' ? null : (parseFloat(adultFee) || 0) }, { merge: true }); }
    finally { setOverheadSaving(false); }
  };
  const classHours = c => {
    if (!c.startTime || !c.endTime) return 2;
    const [sh, sm] = c.startTime.split(':').map(Number);
    const [eh, em] = c.endTime.split(':').map(Number);
    const h = (eh + em / 60) - (sh + sm / 60);
    return h > 0 ? Math.round(h * 100) / 100 : 2;
  };
  const _ohNum = parseFloat(overhead) || 0;
  const _feeNum = parseFloat(sessionFee) || 0;
  const _adultFeeNum = adultFee === '' ? _feeNum : (parseFloat(adultFee) || 0);
  const pnlRows = pnlClasses
    .filter(c => c.date <= _todayStr && !['canceled', 'cancelled'].includes((c.status || '').toLowerCase()))
    .map(c => {
      const isAdult = (c.sessionType || '').toLowerCase().startsWith('adult');
      const hrs = c.imported ? (c.importedHours || 2) : classHours(c);
      const head = coaches.find(x => x.id === c.headCoachId);
      const asstIds = c.assistantCoachIds || [];
      const assts = coaches.filter(x => asstIds.includes(x.id));
      const coachPay = Math.round((hrs * (parseFloat(head?.headRatePerHour) || 0)
        + assts.reduce((t, x) => t + hrs * (parseFloat(x.assistantRatePerHour) || parseFloat(x.headRatePerHour) || 0), 0)) * 100) / 100;
      const att = attendance.filter(x => x.classId === c.id && x.checkedInAt && x.type !== 'coach');
      const paidCount = c.imported ? (c.importedCounts?.paid || 0) : att.length;
      const compCount = c.imported ? (c.importedCounts?.comped || 0) : 0;
      const income = Math.round(paidCount * (isAdult ? _adultFeeNum : _feeNum) * 100) / 100;
      const coachNames = c.imported && (!head) && c.coachNames?.length ? c.coachNames
        : [head ? `${head.firstName} ${head.lastName}` : null,
        ...assts.map(x => `${x.firstName} ${x.lastName}`)].filter(Boolean);
      return { ...c, hrs, coachNames, headName: coachNames.join(' + ') || '—',
        attendees: c.imported ? paidCount + compCount : att.length, comped: compCount, income, coachPay, oh: _ohNum, isAdult,
        net: Math.round((income - coachPay - _ohNum) * 100) / 100, past: true };
    });
  const pnlTotals = pnlRows.filter(r => r.past).reduce((t, r) => ({
    income: t.income + r.income, coachPay: t.coachPay + r.coachPay, oh: t.oh + r.oh, net: t.net + r.net,
  }), { income: 0, coachPay: 0, oh: 0, net: 0 });
  const buildPnlGrid = () => [
    ['Date', 'Week', 'Type', 'Coaches', 'Hours', 'Attendees', 'Income', 'Coach Pay', 'Overhead', 'Net'],
    ...pnlRows.map(r => [r.date, `W${pnlWeekNum(r.date)}`, r.isAdult ? 'Adult' : 'Junior', r.headName, r.hrs, r.attendees,
      r.income.toFixed(2), r.coachPay.toFixed(2), r.oh.toFixed(2), r.net.toFixed(2)]),
    ['TOTAL', '', '', '', '', pnlTotals.income.toFixed(2), pnlTotals.coachPay.toFixed(2), pnlTotals.oh.toFixed(2), pnlTotals.net.toFixed(2)],
  ];
  const exportPnlXlsx = () => {
    const ws = XLSX.utils.aoa_to_sheet(buildPnlGrid());
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `${pnlYear} P&L`);
    XLSX.writeFile(wb, `${pnlYear}_YTD_PnL.xlsx`);
  };
  const exportPnlCsv = () => {
    const csv = buildPnlGrid().map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = `${pnlYear}_YTD_PnL.csv`; a.click();
    URL.revokeObjectURL(url);
  };
  const canceledClasses = classes.filter(c => c.status === 'canceled').length;
  const totalCheckIns = attendance.length;

  // The expense list, shown both as its own tab and inside the dialog the
  // Staff panel's Expenses tile opens. One definition so the two cannot drift.
  const expensesBody = () => (
    <>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
            <TextField select size="small" label="Year" value={pnlYear} sx={{ width: 120 }}
              onChange={e => setPnlYear(Number(e.target.value))}>
              {pnlYears.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
            </TextField>
            <Box sx={{ flexGrow: 1 }} />
            <Button size="small" variant="outlined" component="label" disabled={expenseImporting}>
              Import spreadsheet
              <input type="file" hidden accept=".xlsx,.xls"
                onChange={e => { handleExpenseFile(e.target.files?.[0]); e.target.value = ''; }} />
            </Button>
            <Button size="small" variant="contained" startIcon={<AddIcon />}
              onClick={() => setExpenseEdit({ date: localToday(), budget: 'Corona', recurring: false, qty: 1 })}>
              Add expense
            </Button>
          </Box>

          {expenseMsg && <Alert severity={/fail|could not|no expense/i.test(expenseMsg) ? 'error' : 'success'} sx={{ mb: 2 }} onClose={() => setExpenseMsg('')}>{expenseMsg}</Alert>}

          {expensePreview && (
            <Alert severity="info" sx={{ mb: 2 }}
              action={
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" onClick={() => setExpensePreview(null)}>Cancel</Button>
                  <Button size="small" variant="contained" disabled={expenseImporting} onClick={commitExpenseImport}>
                    {expenseImporting ? 'Importing…' : 'Import'}
                  </Button>
                </Box>
              }>
              Found {expensePreview.rows.length} expense rows ({expensePreview.isNew} new,{' '}
              {expensePreview.rows.length - expensePreview.isNew} already imported — re-importing updates them).
            </Alert>
          )}

          {/* Click a budget tile to show only that budget's expenses; click it
              again, or Total, to go back to both. The tiles keep their own
              figures either way — they are the year, not the selection. */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            {[['Total', null, expenseTotals.all, '#FDEDEC', '#922B21'],
              ['Corona (facility)', 'Corona', expenseTotals.Corona, '#EFF3F8', '#1E3A5F'],
              ['Torch (club)', 'Torch', expenseTotals.Torch, '#EFF3F8', '#1E3A5F']].map(([label, budget, val, bg, fg]) => {
              const selected = expenseBudget === budget;
              return (
                <Paper key={label} variant="outlined"
                  onClick={() => setExpenseBudget(cur => (budget && cur === budget ? null : budget))}
                  role="button" tabIndex={0} aria-pressed={selected}
                  onKeyDown={ev => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); setExpenseBudget(cur => (budget && cur === budget ? null : budget)); } }}
                  sx={{
                    p: 2, borderRadius: 2, backgroundColor: bg, flex: '1 1 150px', textAlign: 'center',
                    cursor: 'pointer', userSelect: 'none', transition: 'border-color .15s, box-shadow .15s',
                    borderColor: selected ? fg : undefined,
                    borderWidth: selected ? 2 : 1,
                    boxShadow: selected ? `inset 0 -3px 0 ${fg}` : 'none',
                    '&:hover': { borderColor: fg },
                  }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: fg }}>${val.toFixed(2)}</Typography>
                  <Typography variant="caption" sx={{ color: '#555' }}>{label}</Typography>
                </Paper>
              );
            })}
          </Box>

          {expenseEdit && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: '#f8f9fa' }}>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField size="small" label={expenseEdit.recurring ? 'Starts' : 'Date'} type="date" sx={{ width: 165 }}
                  InputLabelProps={{ shrink: true }} value={expenseEdit.date || ''}
                  onChange={e => setExpenseEdit(f => ({ ...f, date: e.target.value }))} />
                <TextField select size="small" label="Budget" sx={{ width: 130 }} value={expenseEdit.budget || 'Corona'}
                  onChange={e => setExpenseEdit(f => ({ ...f, budget: e.target.value }))}>
                  <MenuItem value="Corona">Corona</MenuItem>
                  <MenuItem value="Torch">Torch</MenuItem>
                </TextField>
                {/* freeSolo on purpose: the list is what has been used before, not a
                    closed set. A new category has to be typeable or the first
                    expense of a new kind cannot be recorded at all. */}
                <Autocomplete
                  freeSolo size="small" options={expenseCategories}
                  value={expenseEdit.category || ''}
                  onChange={(_, v) => setExpenseEdit(f => ({ ...f, category: v || '' }))}
                  onInputChange={(_, v) => setExpenseEdit(f => ({ ...f, category: v }))}
                  sx={{ width: 190 }}
                  renderInput={(params) => <TextField {...params} label="Category" />}
                />
                {/* Same shape as Category, and it earns it more: vendors are the
                    field that drifts, and there are far more of them, so the
                    list filters as you type rather than being scrolled. */}
                <Autocomplete
                  freeSolo size="small" options={expenseVendors}
                  value={expenseEdit.vendor || ''}
                  onChange={(_, v) => setExpenseEdit(f => ({ ...f, vendor: v || '' }))}
                  onInputChange={(_, v) => setExpenseEdit(f => ({ ...f, vendor: v }))}
                  sx={{ width: 190 }}
                  renderInput={(params) => <TextField {...params} label="Vendor" />}
                />
                <TextField select size="small" label="Type" sx={{ width: 130 }} value={expenseEdit.recurring ? 'yes' : 'no'}
                  onChange={e => setExpenseEdit(f => ({ ...f, recurring: e.target.value === 'yes' }))}>
                  <MenuItem value="no">One-off</MenuItem>
                  <MenuItem value="yes">Monthly</MenuItem>
                </TextField>
                {expenseEdit.recurring ? (
                  <>
                    <TextField size="small" label="$ / month" type="number" sx={{ width: 120 }} value={expenseEdit.monthlyAmount ?? ''}
                      onChange={e => setExpenseEdit(f => ({ ...f, monthlyAmount: e.target.value }))} />
                    <TextField size="small" label="Months" type="number" sx={{ width: 100 }} value={expenseEdit.months ?? 12}
                      onChange={e => setExpenseEdit(f => ({ ...f, months: e.target.value }))} />
                  </>
                ) : (
                  <>
                    <TextField size="small" label="Qty" type="number" sx={{ width: 90 }} value={expenseEdit.qty ?? ''}
                      onChange={e => setExpenseEdit(f => ({ ...f, qty: e.target.value, amount: '' }))} />
                    <TextField size="small" label="Unit $" type="number" sx={{ width: 110 }} value={expenseEdit.unitCost ?? ''}
                      onChange={e => setExpenseEdit(f => ({ ...f, unitCost: e.target.value, amount: '' }))} />
                    <TextField size="small" label="Amount $" type="number" sx={{ width: 120 }}
                      value={expenseEdit.amount !== undefined && expenseEdit.amount !== ''
                        ? expenseEdit.amount
                        : (Math.round((Number(expenseEdit.qty) || 0) * (Number(expenseEdit.unitCost) || 0) * 100) / 100 || '')}
                      onChange={e => setExpenseEdit(f => ({ ...f, amount: e.target.value }))} />
                  </>
                )}
                <TextField size="small" label="Notes" sx={{ flex: '1 1 200px' }} value={expenseEdit.notes || ''}
                  onChange={e => setExpenseEdit(f => ({ ...f, notes: e.target.value }))} />
                <Button size="small" variant="contained" disabled={expenseSaving} onClick={saveExpense}>
                  {expenseSaving ? '…' : 'Save'}
                </Button>
                <Button size="small" onClick={() => setExpenseEdit(null)}>Cancel</Button>
              </Box>
            </Paper>
          )}

          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 820, '& td, & th': { whiteSpace: 'nowrap' } }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#1E3A5F' }}>
                  {['Date', 'Budget', 'Category', 'Vendor', 'Detail', `Amount (${pnlYear})`, ''].map((h, i) => (
                    <TableCell key={h || `a${i}`} sx={{ color: 'white', fontWeight: 'bold' }}
                      align={['Amount', 'Detail'].some(x => h.startsWith(x)) ? 'right' : 'left'}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {shownExpenses.length === 0 && (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ color: '#888', py: 3 }}>
                    {expenseBudget
                      ? `No ${expenseBudget} expenses recorded for ${pnlYear}.`
                      : `No expenses recorded for ${pnlYear}.`}
                  </TableCell></TableRow>
                )}
                {shownExpenses.map(e => (
                  <TableRow key={e.id} hover>
                    <TableCell>{e.recurring ? `${e.date} +${e.months}mo` : e.date}</TableCell>
                    <TableCell>
                      <Chip size="small" label={e.budget}
                        sx={{ backgroundColor: e.budget === 'Corona' ? '#DEEAF1' : '#E3F2E5',
                          color: e.budget === 'Corona' ? '#1E3A5F' : '#1E6B3C', fontWeight: 600 }} />
                    </TableCell>
                    <TableCell>{e.category || '—'}</TableCell>
                    <TableCell>{e.vendor || '—'}</TableCell>
                    <TableCell align="right" sx={{ color: '#777' }}>
                      {e.recurring
                        ? `$${(Number(e.monthlyAmount) || 0).toFixed(2)}/mo × ${expenseMonthsInYear(e, pnlYear)}`
                        : (e.qty > 1 ? `${e.qty} × $${(Number(e.unitCost) || 0).toFixed(2)}` : '—')}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>${e.yearAmount.toFixed(2)}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => setExpenseEdit({ ...e })}><EditIcon fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" disabled={expenseSaving} onClick={() => removeExpense(e)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {shownExpenses.length > 0 && (
                  <TableRow sx={{ backgroundColor: '#EFF3F8' }}>
                    <TableCell colSpan={5} sx={{ fontWeight: 700 }}>
                      {expenseBudget ? `${expenseBudget} total` : 'Total'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>${shownExpenseTotal.toFixed(2)}</TableCell>
                    <TableCell />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" sx={{ color: '#888', display: 'block', mt: 1.5 }}>
            Expenses are tracked by calendar year, the same scope as the P&amp;L tiles above — changing
            the year here changes the year the whole Financials tab reports on. Click Corona or Torch
            to list just that budget; the tiles keep showing the year's full figures either way.
          </Typography>
    </>
  );

  // Revenue belongs to the Financials page now, so it only appears here when
  // this host is showing the money panels. A "Total Revenue" card on the
  // Reports page would be a fourth number disagreeing with the other three.

  // Whether the panel currently on screen has what it needs. The club-wide read
  // is eight whole collections and takes seconds; the panels that fetch for
  // themselves never wait on it. Only the panel body is gated on this — the
  // header, cards and tabs draw immediately and stay put, because a spinner
  // that replaces the whole page takes the tabs away from under a click.
  const panelReady = !loading || SELF_LOADING.has(active);

  const statCards = [
    { label: 'Active Athletes', value: activeAthletes.length, icon: <PeopleIcon sx={{ fontSize: 32, color: '#2E6DA4' }} />, bg: '#DEEAF1' },
    ...(panels.includes('purchases')
      ? [{ label: 'Total Revenue', value: `$${totalRevenue.toFixed(2)}`, icon: <AttachMoneyIcon sx={{ fontSize: 32, color: '#27AE60' }} />, bg: '#E9F7EF' }]
      : []),
    { label: 'Total Check-Ins', value: totalCheckIns, icon: <CalendarMonthIcon sx={{ fontSize: 32, color: '#E67E22' }} />, bg: '#FEF9E7' },
    { label: 'Coaches', value: coaches.length, icon: <SportsIcon sx={{ fontSize: 32, color: '#8E44AD' }} />, bg: '#F5EEF8' },
  ];

  // Attendance by athlete
  const attendanceByAthlete = athletes.map(a => ({
    ...a,
    checkIns: attendance.filter(att => att.athleteId === a.id).length,
  })).filter(a => a.checkIns > 0).sort((a, b) => b.checkIns - a.checkIns);

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1E3A5F' }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body1" sx={{ color: '#888', mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
        </Box>

        <>
            {/* Stat Cards. Drawn from the first paint and filled in when the
                read lands — see the note on panelReady. */}
            {showStatCards && (
            <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
              {statCards.map(card => (
                <Paper key={card.label} elevation={2} sx={{ p: 3, borderRadius: 3, backgroundColor: card.bg, flex: '1 1 180px', display: 'flex', alignItems: 'center', gap: 2 }}>
                  {card.icon}
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold',
                      color: loading ? '#ccc' : '#1E3A5F' }}>
                      {loading ? '—' : card.value}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#555' }}>{card.label}</Typography>
                  </Box>
                </Paper>
              ))}
            </Box>
            )}

            {note && (
              <Typography variant="body2" sx={{ color: '#777', mb: 1.5 }}>{note}</Typography>
            )}

            {/* Tabs */}
            <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid #eee', backgroundColor: '#f8f9fa', pr: 1 }}>
                <Tabs value={Math.min(tab, panels.length - 1)}
                  onChange={(e, v) => { setTab(v); setSearch(''); }}
                  variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile
                  sx={{ flex: 1, minWidth: 0 }}>
                  {panels.map(key => (
                    <Tab key={key} label={PANELS[key].label}
                      icon={PANELS[key].icon} iconPosition={PANELS[key].icon ? 'start' : undefined} />
                  ))}
                </Tabs>
                <TextField size="small" placeholder="Search…" value={search}
                  onChange={e => setSearch(e.target.value)}
                  sx={{ width: { xs: 130, sm: 200 }, backgroundColor: 'white', flexShrink: 0 }} />
              </Box>

              {!panelReady && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                  <CircularProgress />
                </Box>
              )}

              {panelReady && active === 'overview' && (
                <Box sx={{ p: 3 }}><RevenuePanel /></Box>
              )}

              {panelReady && active === 'membership' && (
                <Box sx={{ p: 3 }}><MembershipPanel /></Box>
              )}

              {panelReady && active === 'expenses' && (
                <Box sx={{ p: 3 }}>{expensesBody()}</Box>
              )}

              {panelReady && active === 'purchases' && (
                <Box sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 3 }}>
                    {[
                      { label: 'Total Collected', value: `$${totalRevenue.toFixed(2)}`, color: '#E9F7EF', textColor: '#27AE60' },
                      { label: 'Pending', value: `$${pendingRevenue.toFixed(2)}`, color: '#FEF9E7', textColor: '#E67E22' },
                      { label: 'Transactions', value: realPurchases.length, color: '#DEEAF1', textColor: '#1E3A5F' },
                    ].map(s => (
                      <Paper key={s.label} elevation={1} sx={{ p: 3, borderRadius: 2, backgroundColor: s.color, flex: '1 1 180px', textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', color: s.textColor }}>{s.value}</Typography>
                        <Typography variant="body2" sx={{ color: '#555' }}>{s.label}</Typography>
                      </Paper>
                    ))}
                  </Box>

                  <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 2 }}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#1E3A5F' }}>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Athlete</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Plan</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Method</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Amount</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {realPurchases.filter(p => qMatch(p.athleteName, p.planName, p.tierName, p.paymentMethod, p.email, p.customerEmail, p.paymentStatus)).length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} align="center" sx={{ py: 4, color: '#aaa' }}>No transactions yet.</TableCell>
                          </TableRow>
                        ) : realPurchases.filter(p => qMatch(p.athleteName, p.planName, p.tierName, p.paymentMethod, p.email, p.customerEmail, p.paymentStatus)).map((p, i) => (
                          <TableRow key={p.id} sx={{ backgroundColor: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                            <TableCell>{p.purchaseDate?.seconds ? new Date(p.purchaseDate.seconds * 1000).toLocaleDateString() : '—'}</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>{(() => {
                              if (p.athleteName) return p.athleteName;
                              const a = p.athleteId ? athletes.find(x => x.id === p.athleteId || x.athleteId === p.athleteId) : null;
                              if (a) return `${a.firstName || ''} ${a.lastName || ''}`.trim();
                              const u = p.userId ? evUsers.find(x => x.id === p.userId || x.uid === p.userId) : null;
                              if (u) return `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email;
                              return p.email || p.customerEmail || '—';
                            })()}</TableCell>
                            <TableCell>{p.planName || p.tierName
                              || ({ day_pass: 'Day Pass', membership_start: 'Membership', membership_one_time: 'Membership', subscription_renewal: 'Subscription Renewal' }[p.purchaseType])
                              || '—'}</TableCell>
                            <TableCell sx={{ textTransform: 'capitalize' }}>{p.paymentMethod || '—'}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>${(p.amountPaid || 0).toFixed(2)}</TableCell>
                            <TableCell align="center">
                              <Chip
                                label={p.paymentStatus}
                                size="small"
                                sx={{
                                  backgroundColor: p.paymentStatus === 'paid' ? '#E9F7EF' : '#FEF9E7',
                                  color: p.paymentStatus === 'paid' ? '#1E6B3C' : '#7B4800',
                                  fontWeight: 600,
                                  textTransform: 'capitalize',
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* Financials (per-practice P&L) Tab */}
              {panelReady && active === 'staff' && (
                <Box sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 3 }}>
                    <TextField select size="small" label="Year" sx={{ width: 120 }} value={pnlYear}
                      onChange={e => setPnlYear(Number(e.target.value))}>
                      {pnlYears.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                    </TextField>
                    <TextField size="small" label="Overhead / practice ($)" type="number" value={overhead}
                      onChange={e => setOverhead(e.target.value)} sx={{ width: 170 }} />
                    <TextField size="small" label="Session fee ($)" type="number" value={sessionFee}
                      onChange={e => setSessionFee(e.target.value)} sx={{ width: 130 }} />
                    <TextField size="small" label="Adult fee ($)" type="number" value={adultFee}
                      placeholder={sessionFee} helperText="" onChange={e => setAdultFee(e.target.value)} sx={{ width: 130 }} />
                    <Button size="small" variant="outlined" disabled={overheadSaving} onClick={saveOverhead}>
                      {overheadSaving ? '…' : 'Save'}
                    </Button>
                    <Chip size="small" label="Junior" sx={{ backgroundColor: '#DEEAF1', color: '#1E3A5F', fontWeight: 600 }} />
                    <Chip size="small" label="Adult" sx={{ backgroundColor: '#E3F2E5', color: '#1E6B3C', fontWeight: 600 }} />
                    <Box sx={{ flexGrow: 1 }} />
                    <Button size="small" variant="outlined" startIcon={<FileDownloadIcon />} onClick={exportPnlXlsx} disabled={!pnlRows.length}>XLSX</Button>
                    <Button size="small" variant="outlined" startIcon={<FileDownloadIcon />} onClick={exportPnlCsv} disabled={!pnlRows.length}>CSV</Button>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 3 }}>
                    {[
                      { label: `Income (${pnlYear} to date)`, value: `$${incomeTotal.toFixed(2)}`,
                        color: '#E9F7EF', textColor: '#27AE60', onClick: () => setIncomeDialog(true),
                        sub: incomeSources.filter(s => s.amount > 0).map(s => s.label.toLowerCase()).join(' · ') || 'no income recorded' },
                      { label: `Expenses (${pnlYear} to date)`, value: `$${(pnlTotals.coachPay + pnlTotals.oh + expenseTotals.all).toFixed(2)}`,
                        color: '#FDEDEC', textColor: '#922B21', onClick: () => setExpenseDialog(true),
                        sub: `$${(pnlTotals.coachPay + pnlTotals.oh).toFixed(2)} coach pay + overhead · $${expenseTotals.all.toFixed(2)} club & ops` },
                      { label: `Net (${pnlYear} to date)`, value: `$${(incomeTotal - pnlTotals.coachPay - pnlTotals.oh - expenseTotals.all).toFixed(2)}`,
                        color: (incomeTotal - pnlTotals.coachPay - pnlTotals.oh - expenseTotals.all) >= 0 ? '#DEEAF1' : '#FDEDEC',
                        textColor: (incomeTotal - pnlTotals.coachPay - pnlTotals.oh - expenseTotals.all) >= 0 ? '#1E3A5F' : '#922B21' },
                    ].map(s2 => (
                      <Paper key={s2.label} elevation={1}
                        onClick={s2.onClick}
                        sx={{ p: 3, borderRadius: 2, backgroundColor: s2.color, flex: '1 1 180px', textAlign: 'center',
                          ...(s2.onClick ? { cursor: 'pointer', transition: 'box-shadow .15s, transform .15s',
                            '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' } } : {}) }}>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', color: s2.textColor }}>{s2.value}</Typography>
                        <Typography variant="body2" sx={{ color: '#555' }}>{s2.label}</Typography>
                        {s2.sub && <Typography variant="caption" sx={{ color: '#777', display: 'block', mt: 0.5 }}>{s2.sub}</Typography>}
                      </Paper>
                    ))}
                  </Box>

                  <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 2, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                    <Table size="small" sx={{ minWidth: 760, '& td, & th': { whiteSpace: 'nowrap' } }}>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#1E3A5F' }}>
                          {['Date', 'Wk', 'Coach', 'Hrs', 'Attendees', 'Income', 'Coach Pay', 'Overhead', 'Net'].map(h => (
                            <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold' }} align={['Date', 'Coach'].includes(h) ? 'left' : 'center'}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {[...pnlRows].sort((a, b) => (b.date || '').localeCompare(a.date || '')).filter(r => qMatch(r.headName, r.date)).length === 0 ? (
                          <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: '#aaa' }}>No practices recorded for {pnlYear}.</TableCell></TableRow>
                        ) : [...pnlRows].sort((a, b) => (b.date || '').localeCompare(a.date || '')).filter(r => qMatch(r.headName, r.date)).map((r, i) => (
                          <TableRow key={r.id} sx={{
                            backgroundColor: r.isAdult
                              ? (i % 2 === 0 ? '#E3F2E5' : '#EFF8F0')
                              : (i % 2 === 0 ? '#DEEAF1' : '#EDF4F9'),
                            opacity: r.past ? 1 : 0.55,
                          }}>
                            <TableCell sx={{ fontWeight: 600 }}>{r.date}</TableCell>
                            <TableCell align="center">W{pnlWeekNum(r.date)}</TableCell>
                            <TableCell>{r.headName}</TableCell>
                            <TableCell align="center">{r.hrs}</TableCell>
                            <TableCell align="center">{r.attendees}</TableCell>
                            <TableCell align="center" sx={{ color: '#27AE60', fontWeight: 600 }}>${r.income.toFixed(2)}</TableCell>
                            <TableCell align="center">${r.coachPay.toFixed(2)}</TableCell>
                            <TableCell align="center">${r.oh.toFixed(2)}</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700, color: r.net >= 0 ? '#1E3A5F' : '#E74C3C' }}>${r.net.toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                        {pnlRows.length > 0 && [
                          { label: 'JUNIOR TRAINING', rows: pnlRows.filter(r => !r.isAdult), bg: '#DEEAF1', color: '#1E3A5F' },
                          { label: 'ADULT TRAINING', rows: pnlRows.filter(r => r.isAdult), bg: '#E3F2E5', color: '#1E6B3C' },
                        ].map(s => {
                          const t = s.rows.reduce((a, r) => ({
                            hrs: a.hrs + r.hrs, attendees: a.attendees + r.attendees,
                            income: a.income + r.income, coachPay: a.coachPay + r.coachPay,
                            oh: a.oh + r.oh, net: a.net + r.net,
                          }), { hrs: 0, attendees: 0, income: 0, coachPay: 0, oh: 0, net: 0 });
                          return (
                            <TableRow key={s.label} sx={{ backgroundColor: s.bg, '& td': { fontWeight: 700, borderTop: '2px solid ' + s.color } }}>
                              <TableCell sx={{ color: s.color }}>{s.label}</TableCell>
                              <TableCell sx={{ color: s.color, fontSize: '0.75rem' }}>{s.rows.length} sessions</TableCell>
                              <TableCell />
                              <TableCell align="center">{Math.round(t.hrs * 100) / 100}</TableCell>
                              <TableCell align="center">{t.attendees}</TableCell>
                              <TableCell align="center" sx={{ color: '#27AE60' }}>${t.income.toFixed(2)}</TableCell>
                              <TableCell align="center">${t.coachPay.toFixed(2)}</TableCell>
                              <TableCell align="center">${t.oh.toFixed(2)}</TableCell>
                              <TableCell align="center" sx={{ color: t.net >= 0 ? s.color : '#E74C3C' }}>${t.net.toFixed(2)}</TableCell>
                            </TableRow>
                          );
                        })}
                        {pnlRows.length > 0 && (
                          <TableRow sx={{ backgroundColor: '#EAF0F6', '& td': { fontWeight: 700, borderTop: '2px solid #1E3A5F' } }}>
                            <TableCell>TOTAL</TableCell>
                            <TableCell />
                            <TableCell />
                            <TableCell align="center">{Math.round(pnlRows.reduce((t, r) => t + r.hrs, 0) * 100) / 100}</TableCell>
                            <TableCell align="center">{pnlRows.reduce((t, r) => t + r.attendees, 0)}</TableCell>
                            <TableCell align="center" sx={{ color: '#27AE60' }}>${pnlTotals.income.toFixed(2)}</TableCell>
                            <TableCell align="center">${pnlTotals.coachPay.toFixed(2)}</TableCell>
                            <TableCell align="center">${pnlTotals.oh.toFixed(2)}</TableCell>
                            <TableCell align="center" sx={{ color: pnlTotals.net >= 0 ? '#1E3A5F' : '#E74C3C' }}>${pnlTotals.net.toFixed(2)}</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Typography variant="caption" sx={{ color: '#888', display: 'block', mt: 1.5 }}>
                    Income and Net in this table are per-practice <strong>session value</strong> (attendees × fee) — a
                    delivery measure, not cash. The tiles above report money actually collected, so the two won't tie out:
                    a punchcard is collected once and consumed across several practices.
                  </Typography>
                </Box>
              )}

              {/* Attendance Tab */}
              {panelReady && active === 'attendance' && (
                <Box sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 3 }}>
                    <TextField select size="small" label="Season" sx={{ minWidth: 220 }}
                      value={(seasonSel >= 0 && seasonSel < seasons.length) ? seasonSel : _autoIdx}
                      onChange={e => { setSeasonSel(Number(e.target.value)); setExpandedAthleteId(null); }}>
                      {seasons.map((r, i) => (
                        <MenuItem key={r.start} value={i}>{r.name} (Season {r.num}) · {r.start} → {r.end}</MenuItem>
                      ))}
                    </TextField>
                    <Box sx={{ flexGrow: 1 }} />
                    <Button size="small" variant="outlined" startIcon={<FileDownloadIcon />} onClick={exportSeasonXlsx} disabled={!season}>XLSX</Button>
                    <Button size="small" variant="outlined" startIcon={<FileDownloadIcon />} onClick={exportSeasonCsv} disabled={!season}>CSV</Button>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 3 }}>
                    {[
                      { label: 'Season Sessions (to date)', value: `${seasonPastCount} / ${seasonClasses.length}`, color: '#DEEAF1', textColor: '#1E3A5F' },
                      { label: 'Active Juniors', value: seasonRows.length, color: '#E9F7EF', textColor: '#27AE60' },
                      { label: 'Avg Attendance', value: seasonRows.length ? Math.round(seasonRows.reduce((t, r) => t + r.pct, 0) / seasonRows.length * 100) + '%' : '—', color: '#FCF6E8', textColor: '#8A6D0B' },
                    ].map(s2 => (
                      <Paper key={s2.label} elevation={1} sx={{ p: 3, borderRadius: 2, backgroundColor: s2.color, flex: '1 1 180px', textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', color: s2.textColor }}>{s2.value}</Typography>
                        <Typography variant="body2" sx={{ color: '#555' }}>{s2.label}</Typography>
                      </Paper>
                    ))}
                  </Box>

                  <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 2, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                    <Table size="small" sx={{ minWidth: 640, '& td, & th': { whiteSpace: 'nowrap' } }}>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#1E3A5F' }}>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Athlete</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>School</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Attended</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Balance</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Attendance %</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {seasonRows.length === 0 ? (
                          <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: '#aaa' }}>No junior attendance in this season.</TableCell></TableRow>
                        ) : seasonRows.map((r, i) => (
                          <React.Fragment key={r.id}>
                            <TableRow hover onClick={() => setExpandedAthleteId(expandedAthleteId === r.id ? null : r.id)}
                              sx={{ cursor: 'pointer', backgroundColor: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                              <TableCell sx={{ fontWeight: 600 }}>{r.firstName} {r.lastName}</TableCell>
                              <TableCell sx={{ color: '#888' }}>{r.schoolName || '—'}</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold', color: '#1E3A5F' }}>{r.attended} / {seasonPastCount}</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold', color: (r.sessionBalance || 0) > 2 ? '#27AE60' : (r.sessionBalance || 0) > 0 ? '#E67E22' : '#E74C3C' }}>{r.sessionBalance || 0}</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold' }}>{Math.round(r.pct * 100)}%</TableCell>
                            </TableRow>
                            {expandedAthleteId === r.id && (
                              <TableRow>
                                <TableCell colSpan={5} sx={{ backgroundColor: '#F4F6F8', p: 2 }}>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {seasonClasses.map(c => {
                                      const went = r._set.has(c.id);
                                      const future = c.date > _todayStr;
                                      return (
                                        <Box key={c.id} sx={{ px: 1, py: 0.5, borderRadius: 1, fontSize: '0.7rem', fontWeight: 600, minWidth: 74, textAlign: 'center',
                                          backgroundColor: went ? '#E9F7EF' : future ? '#EEF1F4' : '#FDEDEC',
                                          color: went ? '#1E6B3C' : future ? '#95A5A6' : '#922B21' }}>
                                          W{weekNum(c.date)} · {c.date.slice(5).replace('-', '/')}<br />{went ? '✓' : future ? '·' : '—'}
                                        </Box>
                                      );
                                    })}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

            </Paper>
        </>

        {/* Logs Tab */}
        {panelReady && active === 'courtUsage' && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Court Usage</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" variant="outlined" color="warning" onClick={resyncMemberUsage} disabled={muLoading || muResyncing}>
                  {muResyncing ? 'Resyncing…' : 'Resync Usage'}
                </Button>
                <Button size="small" variant="outlined" onClick={() => setMuLoaded(false)} disabled={muLoading || muResyncing}>Refresh</Button>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              <Chip size="small" label="Members" sx={{ backgroundColor: CU_MEMBER_COLOR, color: 'white', fontWeight: 600, fontSize: '0.7rem' }} />
              <Chip size="small" label="Customers" sx={{ backgroundColor: CU_CUSTOMER_COLOR, color: 'white', fontWeight: 600, fontSize: '0.7rem' }} />
              <Chip size="small" label="Classes" sx={{ backgroundColor: '#1E3A5F', color: 'white', fontWeight: 600, fontSize: '0.7rem' }} />
            </Box>
            <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 2 }}>
              Hours are court-hours (duration × courts). Member overage $ and policy flags are observation-mode data. Class usage is tracked per class only — attendees are not counted here.
            </Typography>
            {muResyncLog && (
              <Alert severity="info" onClose={() => setMuResyncLog(null)} sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}>
                <strong>Resync results:</strong>
                {muResyncLog.map((line, i) => <Typography key={i} variant="caption" sx={{ display: 'block', fontFamily: 'monospace' }}>{line}</Typography>)}
              </Alert>
            )}
            {muLoading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={28} /></Box>}
            {!muLoading && muLoaded && (
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: CU_MEMBER_COLOR, borderLeft: `4px solid ${CU_MEMBER_COLOR}`, pl: 1 }}>Members</Typography>
            )}
            {!muLoading && muLoaded && memberUsageRows.length === 0 && (
              <Typography sx={{ color: '#888', py: 2, mb: 2 }}>No member bookings yet.</Typography>
            )}
            {!muLoading && memberUsageRows.length > 0 && (
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f0f2f5' }}>
                      <TableCell sx={{ fontWeight: 700 }}>Member</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Tier</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>This Week</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Total Hours</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>Bookings</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>Would-be Overage</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Last Booking</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {memberUsageRows.filter(r => qMatch(r.name, r.tierName)).map((r, i) => {
                      const bucketLine = (label, used, alloc) => {
                        const over = alloc != null && used > alloc;
                        return (
                          <Typography key={label} variant="caption" sx={{ display: 'block', lineHeight: 1.5, color: over ? '#C0392B' : '#333', fontWeight: over ? 700 : 400 }}>
                            {label}: {used.toFixed(1)}{alloc != null ? ` of ${alloc}` : ''} hrs
                          </Typography>
                        );
                      };
                      return (
                        <TableRow key={r.memberId} sx={{ backgroundColor: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                          <TableCell sx={{ fontWeight: 600 }}>{r.name}</TableCell>
                          <TableCell>{r.tierName}</TableCell>
                          <TableCell>
                            {bucketLine('Regular', r.weekRegular, r.tier ? (r.tier.weeklyRegularHours || 0) : null)}
                            {bucketLine('Prime', r.weekPrime, r.tier ? (r.tier.weeklyPrimeHours || 0) : null)}
                            {bucketLine('Weekend', r.weekWeekend, r.tier ? (r.tier.weeklyWeekendHours || 0) : null)}
                          </TableCell>
                          <TableCell>
                            {bucketLine('Regular', r.regular, null)}
                            {bucketLine('Prime', r.prime, null)}
                            {bucketLine('Weekend', r.weekend, null)}
                          </TableCell>
                          <TableCell align="center">{r.bookings}{r.cancelled ? <Typography variant="caption" sx={{ display: 'block', color: '#888' }}>{r.cancelled} cancelled</Typography> : null}</TableCell>
                          <TableCell align="center" sx={{ color: r.overage > 0 ? '#E67E22' : '#27AE60', fontWeight: 700 }}>${r.overage.toFixed(2)}</TableCell>
                          <TableCell>{r.lastDate || '—'}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {!muLoading && memberUsageRows.some(r => r.flagged.length > 0) && (
              <>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Flagged Bookings (would-be overages)</Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f0f2f5' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Member</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Time</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Courts</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Flags</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>Overage</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {memberUsageRows.flatMap(r => r.flagged.map(b => ({ ...b, _name: r.name })))
                        .filter(b => qMatch(b._name, (b.policyFlags || []).join(' ')))
                        .sort((a, b) => (b.date || '').localeCompare(a.date || ''))
                        .map((b, i) => (
                          <TableRow key={b.id} sx={{ backgroundColor: i % 2 === 0 ? '#FDF6F0' : 'white' }}>
                            <TableCell>{b.date}</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>{b._name}</TableCell>
                            <TableCell>{b.startTime}–{b.endTime}</TableCell>
                            <TableCell>{(b.courts || []).join(', ')}</TableCell>
                            <TableCell>
                              {(b.policyFlags || []).map(f => (
                                <Chip key={f} size="small" label={f.replace(/_/g, ' ')}
                                  sx={{ mr: 0.5, mb: 0.25, backgroundColor: 'rgba(231,76,60,0.12)', color: '#C0392B', fontWeight: 600, fontSize: '0.68rem' }} />
                              ))}
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700, color: (b.overageAmount || 0) > 0 ? '#E67E22' : '#888' }}>
                              ${(b.overageAmount || 0).toFixed(2)}
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {/* ── Customers ─────────────────────────────────────────────── */}
            {!muLoading && muLoaded && (
              <>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, mt: 3, color: CU_CUSTOMER_COLOR, borderLeft: `4px solid ${CU_CUSTOMER_COLOR}`, pl: 1 }}>Customers</Typography>
                {customerUsageRows.length === 0 && (
                  <Typography sx={{ color: '#888', py: 2, mb: 2 }}>No customer reservations yet. Link a customer in the Schedule's New Event dialog or the Court Rentals booking dialog.</Typography>
                )}
                {customerUsageRows.length > 0 && (
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#f0f2f5' }}>
                          <TableCell sx={{ fontWeight: 700 }}>Customer</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700 }}>Schedule Events</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700 }}>Court Rentals</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700 }}>Court Hours</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Last Reservation</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {customerUsageRows.filter(r => qMatch(r.name)).map((r, i) => (
                          <TableRow key={r.customerId} sx={{ backgroundColor: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                            <TableCell sx={{ fontWeight: 600 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: CU_CUSTOMER_COLOR, flexShrink: 0 }} />
                                {r.name}
                              </Box>
                            </TableCell>
                            <TableCell align="center">{r.events || '—'}</TableCell>
                            <TableCell align="center">{r.rentals || '—'}</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 600 }}>{r.hours.toFixed(1)}</TableCell>
                            <TableCell>{r.lastDate || '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}

            {/* ── Classes (class-level only — no attendee tracking) ─────── */}
            {!muLoading && muLoaded && (
              <>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, mt: 3, color: '#1E3A5F', borderLeft: '4px solid #1E3A5F', pl: 1 }}>Classes</Typography>
                {classUsageRows.length === 0 && (
                  <Typography sx={{ color: '#888', py: 2, mb: 2 }}>No classes found.</Typography>
                )}
                {classUsageRows.length > 0 && (
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#f0f2f5' }}>
                          <TableCell sx={{ fontWeight: 700 }}>Session Type</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700 }}>Classes</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700 }}>Court Hours</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Last Class</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {classUsageRows.filter(r => qMatch(cuSessionLabel(r.type))).map((r, i) => (
                          <TableRow key={r.type} sx={{ backgroundColor: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                            <TableCell>
                              <Chip size="small" label={cuSessionLabel(r.type)}
                                sx={{ backgroundColor: CU_CLASS_COLORS[r.type] || '#7F8C8D', color: 'white', fontWeight: 600, fontSize: '0.7rem' }} />
                            </TableCell>
                            <TableCell align="center">{r.count}</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 600 }}>{r.hours.toFixed(1)}</TableCell>
                            <TableCell>{r.lastDate || '—'}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow sx={{ backgroundColor: '#EFF3F8' }}>
                          <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700 }}>{classUsageRows.reduce((s, r) => s + r.count, 0)}</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700 }}>{classUsageRows.reduce((s, r) => s + r.hours, 0).toFixed(1)}</TableCell>
                          <TableCell />
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}
          </Box>
        )}

      </Box>

        {/* Log Detail Dialog */}
        {/* Income by source — opened from the P&L "Income" tile */}
        <Dialog open={incomeDialog} onClose={() => { setIncomeDialog(false); setIncomeSourceSel(null); }} maxWidth="lg" fullWidth>
          <DialogTitle sx={{ backgroundColor: '#1E3A5F', color: 'white', fontWeight: 700 }}>
            Income by Source · {pnlYear}
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
              <TextField select size="small" label="Year" value={pnlYear} sx={{ width: 120 }}
                onChange={e => { setPnlYear(Number(e.target.value)); setIncomeSourceSel(null); }}>
                {pnlYears.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </TextField>
              <Box sx={{ flexGrow: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#27AE60' }}>${incomeTotal.toFixed(2)}</Typography>
            </Box>

            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#1E3A5F' }}>
                    {['Source', 'Count', 'Amount', ''].map((h, i) => (
                      <TableCell key={h || `i${i}`} sx={{ color: 'white', fontWeight: 'bold' }}
                        align={h === 'Amount' || h === 'Count' ? 'right' : 'left'}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {incomeSources.map(s => (
                    <TableRow key={s.key} hover
                      onClick={() => s.rows.length && setIncomeSourceSel(incomeSourceSel === s.key ? null : s.key)}
                      sx={{ cursor: s.rows.length ? 'pointer' : 'default',
                        backgroundColor: incomeSourceSel === s.key ? '#EAF0F6' : undefined }}>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {s.label}
                        {s.note && <Typography variant="caption" sx={{ color: '#999', ml: 1 }}>({s.note})</Typography>}
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#777' }}>{s.count || '—'}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>${s.amount.toFixed(2)}</TableCell>
                      <TableCell sx={{ color: '#999', width: 90 }}>
                        {s.rows.length ? (incomeSourceSel === s.key ? 'hide' : 'details') : ''}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ backgroundColor: '#EAF0F6' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                    <TableCell />
                    <TableCell align="right" sx={{ fontWeight: 700 }}>${incomeTotal.toFixed(2)}</TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            {incomeSourceSel && (
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, maxHeight: 320, overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {['Date', 'Item', 'Who', 'Amount'].map(h => (
                        <TableCell key={h} sx={{ backgroundColor: '#EFF3F8', fontWeight: 700 }}
                          align={h === 'Amount' ? 'right' : 'left'}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(incomeSources.find(s => s.key === incomeSourceSel)?.rows || []).map(r => (
                      <TableRow key={r.id} hover>
                        <TableCell>{r.date}</TableCell>
                        <TableCell>{r.label}</TableCell>
                        <TableCell sx={{ color: '#777' }}>{r.who || (r.count > 1 ? `${r.count} teams` : '')}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>${r.amount.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Typography variant="caption" sx={{ color: '#888', display: 'block', mt: 1.5 }}>
              Cash basis — money collected in {pnlYear}, matching how expenses are recorded. Tournament revenue counts
              active registrations at their Stripe amount, falling back to the division price for onsite payments.
              Customer invoices count on their paid date; unpaid invoices are receivables and are excluded.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => { setIncomeDialog(false); setIncomeSourceSel(null); }}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Club & operational expenses — opened from the P&L "Expenses" tile */}
        <Dialog open={expenseDialog} onClose={() => { setExpenseDialog(false); setExpenseEdit(null); setExpensePreview(null); setExpenseMsg(''); }} maxWidth="lg" fullWidth>
          <DialogTitle sx={{ backgroundColor: '#1E3A5F', color: 'white', fontWeight: 700 }}>
            Club &amp; Operational Expenses
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {expensesBody()}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => { setExpenseDialog(false); setExpenseEdit(null); setExpensePreview(null); setExpenseMsg(''); }}>Close</Button>
          </DialogActions>
        </Dialog>

    </Layout>
  );
}

export default ClubReports;
